import React from "react";
import { motion } from "framer-motion";
import { Clock, TrendingUp, Info } from "lucide-react";

/**
 * TimeTravelSlider — slider 0 / 1 / 3 / 6 months that visualises the
 * projected metabolic improvement IF the user follows the optimisation
 * plan. Triggers an `onChange(months)` callback so the parent can
 * re-render the anatomy with projected zone scores.
 */

const STOPS = [0, 1, 3, 6];

const T = {
  bg: {
    title:        "Прогноза за бъдещето",
    subtitle:     "Преместете слайдера, за да видите как метаболитната среда МОЖЕ да се промени, ако следвате персонализирания план.",
    now:          "сега",
    months:       "м",
    delta:        "Потенциална промяна на Breast Metabolic Score",
    activeIntervs: "активни лоста",
    caveat:       "Образователна проекция, не клинична прогноза.",
  },
  en: {
    title:        "Future projection",
    subtitle:     "Move the slider to see how the metabolic environment MIGHT change if you follow the personalised plan.",
    now:          "now",
    months:       "mo",
    delta:        "Potential change in Breast Metabolic Score",
    activeIntervs: "active levers",
    caveat:       "Educational projection, not a clinical forecast.",
  },
  es: {
    title:        "Proyección futura",
    subtitle:     "Mueva el control para ver cómo el entorno metabólico PODRÍA cambiar si sigue el plan.",
    now:          "ahora",
    months:       "m",
    delta:        "Cambio potencial en Breast Metabolic Score",
    activeIntervs: "palancas activas",
    caveat:       "Proyección educativa, no un pronóstico clínico.",
  },
};

export default function TimeTravelSlider({
  months,
  onChange,
  timeTravel,
  currentMetabolic,
  lang = "bg",
  isDark = false,
}) {
  const t = T[lang] || T.bg;
  const delta = timeTravel?.metabolic?.delta || 0;
  const projectedTotal = timeTravel?.metabolic?.total || currentMetabolic?.total || 0;
  const interventions = timeTravel?.intervention_count || 0;

  // For the active value, show projection ONLY when months > 0
  const showDelta = months > 0 && delta !== 0;
  const deltaColor = delta > 0 ? "#22C55E" : delta < 0 ? "#EF4444" : "#9CA3AF";

  return (
    <div className="my-4" data-testid="time-travel-slider">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-pink-500" />
          <span className="font-semibold text-sm">{t.title}</span>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${
          isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"
        }`}>
          {interventions} {t.activeIntervs}
        </span>
      </div>

      <p className="text-[11px] opacity-60 mb-3">{t.subtitle}</p>

      {/* Stops */}
      <div className="flex items-center gap-2">
        {STOPS.map((m) => {
          const active = months === m;
          return (
            <button
              key={m}
              onClick={() => onChange(m)}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                active
                  ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md scale-[1.03]"
                  : isDark
                    ? "bg-white/5 text-gray-300 hover:bg-white/10"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid={`tt-stop-${m}`}
            >
              {m === 0 ? t.now : `+${m}${t.months}`}
            </button>
          );
        })}
      </div>

      {/* Delta visualisation */}
      {showDelta && (
        <motion.div
          key={months}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-3 p-3 rounded-xl ${
            isDark ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-emerald-50 border border-emerald-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] opacity-70 mb-0.5">{t.delta}</div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold" style={{ color: deltaColor }} data-testid="tt-delta">
                  {delta > 0 ? "+" : ""}{delta}
                </span>
                <TrendingUp className="w-4 h-4" style={{ color: deltaColor }} />
                <span className="text-sm opacity-70" data-testid="tt-projected-total">
                  → {projectedTotal}/100
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] opacity-70">{months} {months === 1 ? (lang === "bg" ? "месец" : lang === "es" ? "mes" : "month") : (lang === "bg" ? "месеца" : lang === "es" ? "meses" : "months")}</div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-2 flex items-start gap-1.5 text-[10px] opacity-60">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        <span>{timeTravel?.caveat || t.caveat}</span>
      </div>
    </div>
  );
}
